import React, { useEffect, useRef, useState } from 'react';
import styles from './LoginBackground.module.css';
import { VARIANT_KEYS, createVariant } from './variants';
import type { Stage, VariantKey } from './variants';

/**
 * Fondo animado del login en celular. En cada visita se elige al azar una de
 * cinco animaciones (comandas, ingredientes, tickets de caja, platillos y
 * utensilios) y se queda esa: cambiarla mientras alguien escribe su contraseña
 * distrae. Nunca repite la de la visita anterior.
 *
 * Con "reducir movimiento" se dibuja una sola vez, quieta.
 */

const STORAGE_KEY = 'massrestaurant.loginBackground';

const pickVariant = (): VariantKey => {
  let last: string | null = null;
  try {
    last = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Almacenamiento bloqueado (modo privado): sólo se pierde el "no repetir".
  }
  const pool = VARIANT_KEYS.filter((k) => k !== last);
  const pick = pool[Math.floor(Math.random() * pool.length)];
  try {
    window.localStorage.setItem(STORAGE_KEY, pick);
  } catch {
    // ídem
  }
  return pick;
};

const readPrimary = () =>
  getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() || '#128078';

const LoginBackground: React.FC<{ className?: string }> = ({ className }) => {
  const [variantKey] = useState(pickVariant);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const variant = createVariant(variantKey);
    const stage: Stage = { ctx, width: 0, height: 0, dpr: 1, primary: readPrimary() };
    let frame = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      // Cambio de ancho (giro del teléfono): se reparten de nuevo las piezas.
      // Un cambio sólo de alto (teclado en pantalla) las deja donde están.
      const reseed = rect.width !== stage.width || dpr !== stage.dpr;
      stage.width = rect.width;
      stage.height = rect.height;
      stage.dpr = dpr;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (reseed) variant.seed(stage);
      variant.frame(stage, false);
    };

    const tick = () => {
      variant.frame(stage, true);
      frame = requestAnimationFrame(tick);
    };

    resize();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    observer?.observe(canvas);
    if (!reduce) frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [variantKey]);

  return (
    <div className={[styles.root, styles[variantKey], className].filter(Boolean).join(' ')} aria-hidden="true">
      <canvas ref={canvasRef} className={styles.canvas} />
    </div>
  );
};

export default LoginBackground;
